import { useEffect, useState } from "react";
/**
 * @typedef {Object} LocationNew
 * @property {string} city
 * @property {string} region - may be the short or long name of US state (TODO - normalize to one format?)
 * @property {string?} query - could be "Atlanta, GA" (,USA?) string or atlanta-ga from a URL stub
 * @property {string?} shortName - always translate to "Atlanta, GA" (or whatever name came from a Google Place?)
 * @property {Object} place - the returned Google Place if we have it
 * @property {Object} latlng - the lat/long if we know or fetched it
 */

// TODO - combine useGeoLocation and useIpLocation into a single hook
//        that tries/prompts browser geolocation first
//        and only falls back to IP location if that fails
// TODO - maybe also allow caller to pass in a string and/or Google Place object
//        and return a best-guess Google Place for any input
//        Because we can't conditionally call this hook, but the hook can just not do anything if the user or URL already has a location

/**
 *
 * @returns {Promise<LocationNew>}
 */
function getBrowserGeoLocation() {
  return new Promise(async (resolve, reject) => {
    if ("permissions" in navigator) {
      // TODO - async in new Promise() feels wrong; can we chain these properly?
      await navigator.permissions
        .query({ name: "geolocation" })
        .then((permissionStatus) => {
          if (
            permissionStatus.state !== "granted" &&
            permissionStatus.state !== "prompt"
          ) {
            reject(
              `Geolocation not allowed, permission state was ${permissionStatus.state}`,
            );
            //getLocation();
          }
        });
    }
    if (!navigator.geolocation.getCurrentPosition) {
      reject("Geolocation is not supported by this browser.");
    }

    console.debug("Fetching geolocation");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        console.debug(
          `Got browser geolocation, looking up city for ${lat}, ${lng}`,
        );

        // TODO - can we use useJsApiLoader here?
        const { Geocoder } =
          await window.google.maps.importLibrary("geocoding");
        const geocoder = new Geocoder();
        try {
          const response = await geocoder.geocode({ location: { lat, lng } });
          // TODO - should we look for the result with a type of locality? Or just extract that level from the first result?

          const results = response.results;
          console.debug(`got ${results.length} reverse lookup results`);
          const addressComponents = results[0]?.address_components;
          const city = addressComponents.find(
            (item) =>
              item.types.includes("locality") ||
              item.types.includes("administrative_area_level_2"),
          );
          const state = addressComponents.find((item) =>
            item.types.includes("administrative_area_level_1"),
          );
          const country = addressComponents.find((item) =>
            item.types.includes("country"),
          );
          //setCity(`${city ? city.long_name : ""}`);
          //setCurrentState(state.short_name || "");
          console.debug(
            `using ${city?.long_name}, ${state?.short_name} from browser location`,
          );
          resolve({
            city: city?.long_name ?? "",
            region: state?.short_name ?? "",
            latlng: lat + "," + lng,
          });
        } catch (error) {
          reject(error.message);
        }

        /*
                fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GMAPS_API_KEY}`,
                )
                    .then((response) => response.json())
                    .then((data) => {
                        if (data.status === "OK") {
                            const results = data.results[0].address_components;

                            const city = results.find(
                                (item) =>
                                    item.types.includes("locality") ||
                                    item.types.includes("administrative_area_level_2"),
                            );
                            const state = results.find((item) =>
                                item.types.includes("administrative_area_level_1"),
                            );
                            const country = results.find((item) =>
                                item.types.includes("country"),
                            );
                            //setCity(`${city ? city.long_name : ""}`);
                            //setCurrentState(state.short_name || "");
                            resolve({
                                city: city?.long_name ?? "",
                                region: state?.short_name ?? "",
                                latlng: lat+","+lng
                            });
                        } else {
                            // TODO - referer-restricted API keys can't be used on the /maps/api
                            // but we should be able to use
                            // const {Geocoder} = await google.maps.importLibrary("geocoding")
                            // results = await Geocoder.geocode({location:{lat:41.85, lng:-87.65}})
                            // find by types.includes('locality') like above
                            console.log("no location");
                            console.log(data);
                            reject("Unable to get location.");
                        }
                    })
                    .catch((error) => reject(error.message));
                 */
      },
      (error) => {
        reject(error.message);
      },
    );
  });
}

/**
 *
 * @returns {Promise<LocationNew>}
 */
async function getIpGeoLocation() {
  try {
    if (process.env.REACT_APP_IP_LOCATION_API_KEY || "") {
      console.debug("Fetching IP location");
      return fetch(
        `https://ipinfo.io/json?token=${process.env.REACT_APP_IP_LOCATION_API_KEY}`,
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        })
        .then((data) => {
          console.debug("got IP location", data);
          //setCity(data.city);
          //setCurrentState(data.region || "");
          //setLocation(data.city + ", " + data.region);
          return {
            city: data.city,
            region: data.region,
            latlng: data.loc, // string like "41.7189,-83.7130"
          };
        })
        .catch((error) => {
          console.log(
            "There was a problem with the fetch operation:",
            error.message,
          );
        });
    }
  } catch (error) {
    console.error("Error fetching IP location:", error);
    // TODO should this throw like a Promise?
  }
}

// TODO - should this be stored in a context so we only ever run it once?
/**
 *
 * @returns {Location}
 */
const useIpLocation = () => {
  const [ipLocation, setIpLocation] = useState(/** @type {Location} */ {});
  useEffect(() => {
    const getIpLocation = async () => {
      getIpGeoLocation().then((location) => setIpLocation(location));
    };
    getIpLocation();
  }, []); // No deps, just run once

  return ipLocation;
};

// TODO - should any Geo/IP be stored in a context so we only ever run it once?
// TODO - should this always run both methods and return the best available? e.g. can/should it return the IP location if the browser geo prompt is still up?
//        or do we just want to prompt and only use IP if they decline or otherwise fails?
// May still need to update callers (SearchPage.js) to not send a hard redirect the first time this hook returns?
/**
 *
 * @returns {LocationNew}
 */
const useBestLocation = () => {
  const [geoLocation, setGeoLocation] = useState(/** @type {LocationNew} */ {});
  const [ipLocation, setIpLocation] = useState(/** @type {LocationNew} */ {});
  const goog = window.google;

  // TODO - set flags to record and return whether we're still hoping to get a better result?
  //        (if we run both queries as effects async from each other)
  useEffect(() => {
    const getBestLocation = async () => {
      getBrowserGeoLocation()
        .then((location) => setGeoLocation(location))
        .catch(async (error) => {
          console.error("Error fetching browser location:", error);
          const location = await getIpGeoLocation();
          return setIpLocation(location);
        });
    };
    getBestLocation();
  }, [goog]); // No deps, just run once, but make sure google has loaded

  return geoLocation.region ? geoLocation : ipLocation;
};

export default useBestLocation;
